import {
    Button,
    FormField,
    FormTextField,
    FormTextFieldBase,
    GuestStatus,
    IconButton,
    IGuestInfo,
    Label,
    SectionLabel,
    Segmented,
} from '@localina/core';
import {
    AddCircleIcon,
    CheckboxDisabledIcon,
    CheckboxDisabledSelectedIcon,
    CookieIcon,
    FavoriteBorderIcon,
    FavoriteIcon,
    TextBlockIcon,
    TrashIcon,
} from '@localina/icons';
import React from 'react';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { UserFeature } from '../UserFeature';

interface IProps {
    guestInfo: IGuestInfo;
}

export const GuestInfo: React.FC<IProps> = (props) => {
    const { t } = useTranslation();

    const { formState } = useFormContext();
    const { defaultValues } = formState;

    const salutationOptions = [
        { value: 'MALE', label: t('guest.details.view.fields.salutation.male') },
        { value: 'FEMALE', label: t('guest.details.view.fields.salutation.female') },
        { value: 'OTHER', label: t('guest.details.view.fields.salutation.other') },
    ];

    const guestStatusOptions = [
        { value: GuestStatus.VIP, label: t('guest.details.view.fields.guestStatus.vip') },
        { value: GuestStatus.BLACKLIST, label: t('guest.details.view.fields.guestStatus.blacklist') },
    ];

    return (
        <div className="guest-info">
            <SectionLabel value={t('guest.details.view.fields.contact')} />
            {}
            <UserFeature filter="guestStatus">
                <div className="guest-info-choice">
                    <Label type="label" value={t('guest.details.view.fields.guestStatusLabel')} />
                    <FormField
                        name="guestStatus"
                        className="guest-info-guest-status"
                        accepter={Segmented}
                        fullWidth
                        options={guestStatusOptions}
                        enableDeselect
                    />
                </div>
            </UserFeature>
            <div className="guest-info-choice">
                <Label type="label" value={t('guest.details.view.fields.salutationLabel')} />
                <FormField
                    name="salutation"
                    className="guest-info-salutation"
                    accepter={Segmented}
                    fullWidth
                    options={salutationOptions}
                    enableDeselect
                />
            </div>
            <FormTextField name="lastName" label={t('guest.details.view.fields.lastName')} required />
            <FormTextField name="firstName" label={t('guest.details.view.fields.firstName')} />
            <FormTextField name="company" label={t('guest.details.view.fields.company')} />
            <FormTextField
                name="email"
                label={t('guest.details.view.fields.email')}
                required={Boolean(defaultValues?.email)}
            />
            <FormTextField
                name="allergies"
                label={t('guest.details.view.fields.allergies')}
                multiline
                icon={<CookieIcon />}
            />
            <FormTextField
                name="restaurantComment"
                label={t('guest.details.view.fields.restaurantComment')}
                multiline
                icon={<TextBlockIcon />}
            />
            <SectionLabel value={t('guest.details.view.list.phone')} />
            <GuestPhoneNumbers />
            <SectionLabel value={t('guest.details.view.fields.advertising')} />
            {}
            <div className="guest-info-newsletter">
                {props.guestInfo.subscribeToNewsletter ? <CheckboxDisabledSelectedIcon /> : <CheckboxDisabledIcon />}
                <Label
                    type="label"
                    value={t(
                        `guest.details.view.fields.newsletter.${
                            props.guestInfo.subscribeToNewsletter ? 'subscribed' : 'notSubscribed'
                        }`,
                    )}
                />
            </div>
        </div>
    );
};

const GuestPhoneNumbers = () => {
    const { t } = useTranslation();
    const { formState } = useFormContext();
    const { fields, append, remove, swap } = useFieldArray({
        name: 'phoneNumbers',
    });
    const { defaultValues } = formState;

    const noInitialPhoneNumbers = Boolean(!defaultValues?.['phoneNumbers'].length);

    return (
        <div className="guest-info-phone-numbers">
            {}
            {fields.map((item, index) => {
                const canRemove = noInitialPhoneNumbers || (fields.length > 1 && index > 0);
                return (
                    <Controller
                        key={item.id}
                        name={`phoneNumbers.${index}.phone`}
                        render={({ field, fieldState }) => (
                            <div className="guest-info-phone-numbers__row">
                                <FormTextFieldBase
                                    controllerProps={{
                                        value: field.value,
                                        onChange: field.onChange,
                                        name: field.name,
                                    }}
                                    type="phone"
                                    name={field.name}
                                    errorMessage={fieldState.error?.message || ''}
                                    label=""
                                    icon={
                                        index === 0 ? (
                                            <FavoriteIcon className="favorite-icon-style" />
                                        ) : (
                                            <FavoriteBorderIcon
                                                className="favorite-icon-style"
                                                onClick={() => swap(0, index)}
                                            />
                                        )
                                    }
                                />
                                <IconButton
                                    icon={<TrashIcon />}
                                    disabled={!canRemove}
                                    className="guest-info-phone-numbers__remove"
                                    onClick={() => remove(index)}
                                />
                            </div>
                        )}
                    />
                );
            })}
            {}
            <div className="guest-info-phone-numbers__add">
                <Button
                    secondary
                    label={
                        <>
                            <AddCircleIcon />
                            {t('guest.details.view.actions.addPhoneNumber')}
                        </>
                    }
                    onClick={() => append({ phone: '' })}
                />
            </div>
        </div>
    );
};
